import { Form, Input, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  GET_MOBILE_CODE,
  LOGIN_BY_MOBILE_PWD,
  LOGIN_BY_MOBILE,
  GET_LOGIN_SERIAL_NUMBER,
  POLL_LOGIN_SERIAL_NUMBER_STATUS,
} from '~/graphqls/gqls'
import { useMutation } from '@apollo/client'
import { ResetPwdModal } from './ResetPwdModal'
import { Captcha } from './Captcha'
import Cookies from 'js-cookie'
import CryptoJS from 'crypto-js'
import { MyForm } from '~/components/ui/MyForm'
import { MyModal } from '~/components/ui/MyModal'
import { graphQLErrorMessage, isMobile } from '~/utils'
import { MyBtn } from '~/components/ui/MyBtn'
import Link from 'next/link'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import {
  getMobileCode,
  getMobileCodeVariables,
  loginByMobile,
  loginByMobilePwd,
  loginByMobilePwdVariables,
  loginByMobileVariables,
  getLoginSerialNumber,
  LoginSerialNumberStatus,
  pollLoginSerialNumberStatus,
  pollLoginSerialNumberStatusVariables,
} from '~/graphqls/types'
import { QRCodeSVG } from 'qrcode.react'
import Refresh from '../icon/Refresh'
import classNames from 'classnames'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import CircleOK from '../icon/CircleOK'
import { MyCheckbox } from '../ui/MyCheckbox'

import styles from './LoginModal.module.css'
import { useRouter } from 'next/router'
import { useDeviceInfo } from '~/hooks/useDeviceInfo'

const LinkText = styled(MyBtn)`
  display: block;
  margin: 0 auto;
`

const titleMap = {
  qr: '扫码登录',
  pwd: '账号密码登录',
  code: '手机快捷登录/注册',
}

// 跨域登陆，同时登陆wujieai
function crossLogin(token: string) {
  const backURL = window.location.href
  window.location.href = `${
    process.env.NEXT_PUBLIC_WUJIEAI_HOST
  }/api/cross-login?token=${token}&redirect=${encodeURIComponent(backURL)}`
}

export const LoginModal: React.FC = observer(() => {
  const [isChecked, setIsChecked] = useState(false)
  const [pwdVisible, setPwdVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loginType, setLoginType] = useState<'pwd' | 'code' | 'qr'>('code')
  const [qrStatus, setQrStatus] = useState<
    'networkError' | LoginSerialNumberStatus
  >(LoginSerialNumberStatus.NO_LOGIN)
  const [serialNumber, setSerialNumber] = useState<string | null>()
  const { store } = useStores()
  const router = useRouter()
  const { handleSendDeviceInfo } = useDeviceInfo()

  const visible = useMemo(() => {
    return !!store.modal?.login
  }, [store.modal])

  const setVisible = useCallback(
    (visible: boolean) => {
      store.modal?.setLoginVisible(visible)
    },
    [store.modal]
  )

  const [form] = Form.useForm()

  const [getMobileCodeAction] = useMutation<
    getMobileCode,
    getMobileCodeVariables
  >(GET_MOBILE_CODE)
  const [loginByMobilePwdAction] = useMutation<
    loginByMobilePwd,
    loginByMobilePwdVariables
  >(LOGIN_BY_MOBILE_PWD)
  const [loginByMobileAction] = useMutation<
    loginByMobile,
    loginByMobileVariables
  >(LOGIN_BY_MOBILE)
  const pollLoginSerialNumberStatusAction = useImperativeQuery<
    pollLoginSerialNumberStatus,
    pollLoginSerialNumberStatusVariables
  >(POLL_LOGIN_SERIAL_NUMBER_STATUS)
  const getLoginSerialNumberAction = useImperativeQuery<getLoginSerialNumber>(
    GET_LOGIN_SERIAL_NUMBER
  )

  const beforeStart = useCallback(async next => {
    await form.validateFields(['mobile'])
    next()
  }, [])

  const onMobileCode = useCallback(
    validInfo => {
      const mobile = form.getFieldValue('mobile')

      getMobileCodeAction({
        variables: {
          mobile: mobile,
          behaviorParam: {
            token: validInfo.token,
            authenticate: validInfo.authenticate,
          },
        },
      })
        .then(() => {
          message.destroy()
          message.success('获取验证码成功')
        })
        .catch(error => {
          message.destroy()
          message.error(graphQLErrorMessage(error) || '获取验证码失败')
        })
    },
    [form, getMobileCodeAction]
  )

  const onConfirm = useCallback(async () => {
    const formData = await form.validateFields()
    if (!isChecked) {
      message.destroy()
      message.error('请先勾选同意相关协议')
      return
    }

    setLoading(true)

    if (loginType === 'code') {
      loginByMobileAction({
        variables: {
          mobile: formData.mobile,
          code: formData.code,
        },
      })
        .then(data => {
          Cookies.set('gate-token', data?.data?.data?.token || '', {
            expires: 365,
          })
          setVisible(false)
          router.reload()
          handleSendDeviceInfo()
          // crossLogin(data.data?.data?.tokenVoucher!)
        })
        .catch(error => {
          message.destroy()
          message.error(graphQLErrorMessage(error) || '登录失败')
        })
        .finally(() => {
          setLoading(false)
        })
    } else if (loginType === 'pwd') {
      loginByMobilePwdAction({
        variables: {
          mobile: formData.mobile,
          password: CryptoJS.MD5(formData.password).toString(),
        },
      })
        .then(data => {
          Cookies.set('gate-token', data?.data?.data?.token || '', {
            expires: 365,
          })
          setVisible(false)
          router.reload()
          handleSendDeviceInfo()
          // crossLogin(data.data?.data?.tokenVoucher!)
        })
        .catch(error => {
          message.destroy()
          message.error(graphQLErrorMessage(error) || '登录失败')
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [form, loginType, isChecked])

  /**
   * qr
   */
  const handleLoginSerialNumber = async () => {
    try {
      const res = await getLoginSerialNumberAction()
      setSerialNumber(res.data.loginSerialNumber?.serialNumber)
      setQrStatus(LoginSerialNumberStatus.NO_LOGIN)
    } catch (err) {
      setQrStatus('networkError')
    }
  }
  useEffect(() => {
    if (loginType === 'qr') {
      handleLoginSerialNumber()
    }
  }, [loginType])
  const pollTimer = useRef(0)
  useEffect(() => {
    clearInterval(pollTimer.current)
    if (loginType === 'qr' && serialNumber && visible) {
      pollTimer.current = window.setInterval(async () => {
        try {
          const res = await pollLoginSerialNumberStatusAction({ serialNumber })
          const status = res.data.loginSerialNumberStatus?.status
          if (status === LoginSerialNumberStatus.LOGIN) {
            Cookies.set(
              'gate-token',
              res.data?.loginSerialNumberStatus?.token || '',
              {
                expires: 365,
              }
            )
            setVisible(false)
            setQrStatus(LoginSerialNumberStatus.NO_LOGIN)
            crossLogin(res.data?.loginSerialNumberStatus?.tokenVoucher!)
            handleSendDeviceInfo()
          } else {
            setQrStatus(status as LoginSerialNumberStatus)
          }
        } catch (e) {
          setQrStatus('networkError')
        }
      }, 1500)
    }

    return () => clearInterval(pollTimer.current)
  }, [loginType, serialNumber, visible])

  const QRMask = () => {
    if (
      !serialNumber ||
      qrStatus === 'networkError' ||
      qrStatus === LoginSerialNumberStatus.EXPIRED
    ) {
      return (
        <div
          className={classNames(
            'cursor-pointer absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center',
            styles.qrMask
          )}
          onClick={handleLoginSerialNumber}
        >
          <span className="h-6 w-6">
            <Refresh />
          </span>
          <p className="mt-2 text-center text-neutral-300 text-xs">
            {qrStatus === LoginSerialNumberStatus.EXPIRED
              ? '二维码已失效'
              : '二维码加载失败'}
            <br />
            点击刷新
          </p>
        </div>
      )
    } else if (qrStatus === LoginSerialNumberStatus.SCAN_CODE) {
      return (
        <div
          className={classNames(
            'cursor-pointer absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center',
            styles.qrMask
          )}
        >
          <span className="h-6 w-6">
            <CircleOK />
          </span>
          <p className="mt-2 text-center text-neutral-300 text-xs">
            扫描成功
            <br />
            请在手机上点击确认登录
          </p>
        </div>
      )
    }

    return null
  }

  return (
    <>
      <MyModal
        visible={visible}
        title={titleMap[loginType]}
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        width={448}
        footer={null}
      >
        {loginType === 'qr' ? (
          <div className="-mt-6">
            <p className="text-neutral-400">打开无界版图 APP - 我的 - 扫一扫</p>
            <div className="flex p-4 gap-8 mt-7">
              <div className="p-2 border-solid border-2 border-neutral-600 rounded-lg h-[180px] relative">
                <QRCodeSVG
                  size={160}
                  value={JSON.stringify({
                    serialNumber: serialNumber,
                    action: 'login',
                  })}
                ></QRCodeSVG>
                <QRMask />
              </div>

              <div>
                <img
                  src="/images/login/scan-qr.jpg"
                  alt=""
                  className="w-[144px] h-[198px] block"
                />
              </div>
            </div>
          </div>
        ) : (
          <MyForm form={form}>
            <Form.Item
              name="mobile"
              rules={[
                {
                  required: true,
                  message: '请输入手机号',
                },
                {
                  pattern: isMobile,
                  message: '请输入正确的手机号',
                },
              ]}
            >
              <Input placeholder="手机号" addonBefore="+86" />
            </Form.Item>
            {loginType === 'code' ? (
              <Form.Item
                name="code"
                rules={[{ required: true, message: '请输入手机验证码' }]}
              >
                <Input
                  placeholder="手机验证码"
                  addonAfter={
                    <Captcha
                      onSuccess={onMobileCode}
                      beforeStart={beforeStart}
                    />
                  }
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  placeholder="密码"
                  addonAfter={
                    <a
                      href="#!"
                      onClick={e => {
                        e.preventDefault()
                        setPwdVisible(true)
                      }}
                    >
                      忘记密码
                    </a>
                  }
                />
              </Form.Item>
            )}

            <div style={{ marginBottom: 18 }}>
              <MyCheckbox
                checked={isChecked}
                onChange={e => setIsChecked(e.target.checked)}
              >
                我已阅读并同意《
                <Link href="/us/user-agreement">
                  <a target="_blank" rel="noreferrer" className="primary-color">
                    用户协议
                  </a>
                </Link>
                》和《
                <Link href="/us/privacy">
                  <a target="_blank" rel="noreferrer" className="primary-color">
                    隐私政策
                  </a>
                </Link>
                》
              </MyCheckbox>
            </div>
            <MyBtn
              onClick={onConfirm}
              style={{ marginBottom: 16 }}
              type="primary"
              size="large"
              loading={loading}
              block
            >
              确定
            </MyBtn>
          </MyForm>
        )}

        <div className="flex items-center">
          {[
            { type: 'pwd', label: '账号密码登录' },
            { type: 'code', label: '验证码登录/注册' },
            { type: 'qr', label: '扫码登录' },
          ]
            .filter(item => item.type !== loginType)
            .map(item => {
              return (
                <LinkText
                  key={item.type}
                  onClick={() => setLoginType(item.type as any)}
                  type="link"
                >
                  {item.label}
                </LinkText>
              )
            })}
        </div>
      </MyModal>
      <ResetPwdModal visible={pwdVisible} setVisible={setPwdVisible} />
    </>
  )
})
